import { HTMLInputTypeAttribute, JSX, useEffect, useRef, useState } from "react"
import { SearchIcon, SelectIcon, SocialIcon } from "../ui/icons"
import { localize } from "../../../common/services/localization"

export const Input: React.FC<{
    className?: string
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    placeholderLocal: string
    type?: HTMLInputTypeAttribute
    maxLength?: number
    innerIcon?: JSX.Element
    defaultValue?: string
    value?: string | number
}> = ({ onChange, placeholderLocal, className, type, maxLength, innerIcon, defaultValue, value }) => {
    return <div className={`w-96 pl-[16px] pr-[10px] py-[13px] input-container inline-flex justify-start items-center gap-1.5 ${className ?? ''}`}>
        <div className="p-[2.50px] flex justify-center items-center gap-2.5 ">
            {innerIcon ?? null}
        </div>
        <input
            defaultValue={defaultValue}
            max={maxLength}
            maxLength={maxLength}
            type={type}
            onChange={onChange}
            value={value}
            placeholder={localize(placeholderLocal)}
            className="flex justify-start w-full  items-center text-[14px] lg:text-[18px] font-normal font-['Roboto']">
        </input>
    </div>
}

export const SearchInput: React.FC<{
    className?: string
    onChange?: any
    placeholderLocal: string
    type?: HTMLInputTypeAttribute
    maxLength?: number
}> = ({ onChange, placeholderLocal, className, type, maxLength }) => {
    return <Input
        placeholderLocal={placeholderLocal}
        onChange={onChange}
        className={className}
        innerIcon={<SearchIcon />}
        type={type}
        maxLength={maxLength}
    />
}

export const SocialInput: React.FC<{
    className?: string
    onChange?: (value: string) => void
    type: SocialType
    defaultValue?: string
}> = ({ onChange, className, type, defaultValue }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(e.target.value);
        }
    };

    return <Input
        defaultValue={defaultValue}
        placeholderLocal={`social_input_${type}`}
        onChange={handleChange}
        className={className}
        innerIcon={<SocialIcon type={type == 'discordUserName' ? 'discord' : type} isActive={true} />}
        type={"text"}
        maxLength={300}
    />
}

export const SelectInput: React.FC<CustomSelectInputProps> = ({ onChange, options, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState<SelectOption | undefined>(options[0]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSelect = (option: SelectOption) => {
        setSelected(option);
        setIsOpen(false);
        onChange?.(option.value);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            ref={dropdownRef}
            className={`w-[374px] relative pl-[16px] pr-[10px] py-[13px] bg-transparent border border-white rounded cursor-pointer input-container ${className}`}
        >
            <div
                className="flex justify-between items-center gap-2"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-white font-['Roboto'] text-[14px] lg:text-[18px]">
                    {localize(selected?.localName ?? '')}
                </span>
                <SelectIcon isOpen={isOpen} />
            </div>

            {isOpen && (
                <div className="absolute top-full mt-2 left-0 w-full bg-[#081015] rounded z-50 max-h-[200px] overflow-auto shadow-lg">
                    {options.map((opt) => (
                        <div
                            key={opt.value}
                            onClick={() => handleSelect(opt)}
                            className="px-4 py-2 font-['Roboto'] text-white hover:bg-[#0E1C25] text-[14px] lg:text-[16px]"
                        >
                            {localize(opt.localName)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const SelectWithSearchInput: React.FC<CustomSelectInputProps & {
    value?: string | undefined;
}> = ({
    className,
    onChange,
    options,
    value,
}) => {
        const [isOpen, setIsOpen] = useState(false);
        const [search, setSearch] = useState('');
        const [selected, setSelected] = useState<SelectOption | undefined>(
            options.find(o => o.value === value)
        );

        const dropdownRef = useRef<HTMLDivElement>(null);

        // синхронизация selected с пропом value
        useEffect(() => {
            setSelected(options.find(o => o.value === value));
        }, [value, options]);

        const handleSelect = (option: SelectOption) => {
            setSelected(option);
            setIsOpen(false);
            setSearch('');
            onChange?.(option.value);
        };

        const filteredOptions = options.filter(opt =>
            localize(opt.localName).toLowerCase().includes(search.toLowerCase())
        );

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        return (
            <div
                ref={dropdownRef}
                className={`${className || ''} w-[374px] relative pl-[16px] pr-[10px] py-[13px] bg-transparent border border-white rounded cursor-pointer input-container`}
            >
                <div
                    className="flex justify-between items-center gap-2"
                    onClick={() => setIsOpen(prev => !prev)}
                >
                    <span className="text-white font-['Roboto'] text-[18px]">
                        {localize(selected?.localName ?? '') || 'Выберите...'}
                    </span>
                    <SelectIcon isOpen={isOpen} />
                </div>

                {isOpen && (
                    <div className="absolute top-full mt-2 left-0 w-full bg-[#081015] rounded z-50 shadow-lg max-h-[240px] overflow-auto">
                        <input
                            autoFocus
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Поиск..."
                            className="w-full px-4 py-2 text-white bg-[#0B141B] placeholder-gray-400 border-b border-[#1E2A32] outline-none font-['Roboto']"
                        />
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(opt => (
                                <div
                                    key={opt.value}
                                    onClick={() => handleSelect(opt)}
                                    className="px-4 py-2 font-['Roboto'] text-white hover:bg-[#0E1C25] text-[16px] cursor-pointer"
                                >
                                    {localize(opt.localName)}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-gray-400 text-sm font-['Roboto']">Ничего не найдено</div>
                        )}
                    </div>
                )}
            </div>
        );
    };
